import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';

import { BaseSelectFilter } from '@/components/common';

import { useGetReeferAgesQuery } from '@/graphql/GetReeferAges.generated';

import { appStore, reportRequestError, retryAfterRequestError } from '@/store';

import {
  getReeferAge,
  selectAllReeferAges,
  setReeferAges,
  setSelectedReeferAges,
  store,
  unselectAllReeferAges,
  unselectReeferAge,
} from '../store';

function ReeferAgesSelectFilter() {
  const appSnap = useSnapshot(appStore);
  const snap = useSnapshot(store);

  const [{ data, fetching, error }, reexecuteQuery] = useGetReeferAgesQuery({
    pause: snap.reeferAges.length > 0,
  });

  const [errorId, setErrorId] = useState<string | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (data && data.container_insights_ages) {
      setReeferAges(data.container_insights_ages);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setErrorId(reportRequestError(error, reexecuteQuery));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleRetry = () => {
    retryAfterRequestError(errorId);
    setErrorId(null);
  };

  const handleRemove = (id: string) => () => unselectReeferAge(id);

  const selectedReeferAgesIds = snap.selectedReeferAges.map(
    (reeferAge) => reeferAge.id,
  );
  const allSelected = selectedReeferAgesIds.length === snap.reeferAges.length;

  return (
    <BaseSelectFilter
      id="ages-select-filter"
      label={t('filters.reeferAges')}
      values={selectedReeferAgesIds}
      onChange={setSelectedReeferAges}
      onClear={unselectAllReeferAges}
      loading={fetching}
      error={
        appSnap.hasRequestError(errorId)
          ? 'Error while fetching reefer ages'
          : null
      }
      onRetry={handleRetry}
      renderChips={(selected: string[]) =>
        selected
          .map(getReeferAge)
          .filter((reeferAge) => reeferAge !== null)
          .map((reeferAge) => (
            <Chip
              key={reeferAge.id}
              color="primary"
              variant="outlined"
              label={reeferAge.label}
              onDelete={handleRemove(reeferAge.id)}
            />
          ))
      }
    >
      <ListSubheader
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          {t('filters.reeferAges')}
          <sup
            style={{
              marginLeft: 4,
              visibility:
                selectedReeferAgesIds.length > 0 ? 'visible' : 'hidden',
            }}
          >
            ({selectedReeferAgesIds.length})
          </sup>
        </Box>
        {snap.reeferAges.length > 0 && (
          <Button
            sx={{ textTransform: 'none' }}
            onClick={allSelected ? unselectAllReeferAges : selectAllReeferAges}
          >
            {allSelected ? 'Clear' : 'Select all'}
          </Button>
        )}
      </ListSubheader>
      {snap.reeferAges.map((reeferAge) => (
        <MenuItem key={reeferAge.id} value={reeferAge.id}>
          <Checkbox checked={reeferAge.selected} />
          <ListItemText primary={reeferAge.label} />
        </MenuItem>
      ))}
    </BaseSelectFilter>
  );
}

export { ReeferAgesSelectFilter };
